import "swiper/css"
import { useEffect } from "react"
import { Provider } from "react-redux"
import { createRoot } from "react-dom/client"
import "react-toastify/dist/ReactToastify.css"
import { ToastContainer } from "react-toastify"
import { BrowserRouter } from "react-router-dom"
import { ModalProvider } from "./context/ModalsContext"
import { useEventTheme } from "./hooks/useEventTheme"
import { useAppInit } from "./hooks/useAppInit/useAppInit.tsx"
import "./utils/i18next"
import { initSentry } from "utils/sentry"
import { captureRefCode } from "utils/refCode"
import { applyPlatformAttr } from "utils/platform"
import { isDiagnosticsEnabled } from "utils/debugUsers"
import { diagnosticsRecorder } from "utils/diagnostics"
import { ErrorBoundary } from "./utils/ErrorBoundary.tsx"
import { debugExperiments } from "utils/debugExperiments"
import { AppShell } from "ui/AppShell"
import { DevToolsButton } from "ui/DevToolsButton"
import { SnowfallV2 } from "ui/snowfall-v2/SnowfallV2.tsx"
import { GlobalErrorHandler } from "./ui/GlobalErrorHandler/index.tsx"
import "./index.scss"
import { store } from "./store"
import View from "./pages/index.tsx"
import { ModalsRoot } from "./modals"
import { WithGsap } from "./providers/WithGsap.tsx"
import { DeviceInfoProvider } from "./providers/DeviceInfo.tsx"
import { WebAppViewportProvider } from "./providers/WebAppViewport/WebAppViewport.tsx"

captureRefCode()
applyPlatformAttr()

const __diagEnabled = isDiagnosticsEnabled()

if (__diagEnabled) {
  initSentry()
  diagnosticsRecorder.start()
  debugExperiments.init()
}

const App = () => {
  useAppInit()
  useEventTheme()

  useEffect(() => {
    const interval = setInterval(() => {
      window.scrollTo(0, 0)
    }, 50)

    return () => clearInterval(interval)
  }, [])

  return (
    <>
      <div id="version">v{import.meta.env.VITE_APP_VERSION}</div>
      <GlobalErrorHandler />
      <ErrorBoundary>
        <View />
        <ModalsRoot />
        <SnowfallV2 />
        {__diagEnabled && <DevToolsButton />}
        <ToastContainer
          position="bottom-center"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
          stacked
        />
      </ErrorBoundary>
    </>
  )
}

createRoot(document.getElementById("root")!).render(
  <Provider store={store}>
    <BrowserRouter basename="/">
      <WebAppViewportProvider>
        <DeviceInfoProvider>
          <WithGsap>
            <ModalProvider>
              <AppShell>
                <App />
              </AppShell>
            </ModalProvider>
          </WithGsap>
        </DeviceInfoProvider>
      </WebAppViewportProvider>
    </BrowserRouter>
  </Provider>
)
